import { alpha, createTheme } from '@mui/material/styles';
import accessibleModalManager from './components/AccessibleModalManager';

// Edit shared colors/borders here; both MUI and SCSS consume these tokens.
export const SHARED_DESIGN = {
  text: '#f8fafc', muted: '#b8c4d4', border: 'rgba(255,255,255,.28)',
  danger: '#fb7185', success: '#22c55e', radius: 12, accentText: '#091320',
};
export const panelSx = {
  border: 1, borderColor: 'divider', borderRadius: 1,
  bgcolor: 'background.paper', color: 'text.primary', p: { xs: 2, sm: 3 }, mb: 3,
};
export const feedbackRecordSx = {
  my: 1,
  '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } },
};
export const getThemeVariables = (name) => {
  const colors = THEME_COLORS[name] || THEME_COLORS.teal;
  return { bg: colors.bg, 'bg-soft': colors.soft, surface: colors.surface,
    'surface-raised': colors.raised, accent: colors.primary, 'accent-warm': colors.secondary,
    text: SHARED_DESIGN.text, 'text-muted': SHARED_DESIGN.muted, border: SHARED_DESIGN.border,
    danger: SHARED_DESIGN.danger, radius: `${SHARED_DESIGN.radius}px`,
    'accent-text': SHARED_DESIGN.accentText, 'accent-border': alpha(colors.primary, .5),
    'warm-glow': alpha(colors.secondary, .1),
    glow: colors.glow, desk: colors.desk, felt: colors.felt,
    'surface-gradient': `linear-gradient(145deg, ${colors.raised}, ${colors.surface})` };
};

// Shared visual language for every MUI surface. Component-specific layout
// remains in each page so this theme cannot alter application behavior.
export const THEME_COLORS = {
  teal: { primary: '#2dd4bf', secondary: '#f59e0b', bg: '#090f1b', soft: '#111c2e', surface: '#121d2e', raised: '#19283f', glow: 'rgba(45,212,191,.14)', desk: 'rgba(3,45,34,.34)', felt: 'linear-gradient(145deg, #126247, #06432f 68%)' },
  sapphire: { primary: '#60a5fa', secondary: '#fbbf24', bg: '#080f24', soft: '#122647', surface: '#13233e', raised: '#1b355b', glow: 'rgba(96,165,250,.18)', desk: 'rgba(20,55,105,.4)', felt: 'linear-gradient(145deg, #235a95, #102f55 68%)' },
  plum: { primary: '#d8b4fe', secondary: '#fda4af', bg: '#160c20', soft: '#2a1939', surface: '#281c36', raised: '#3e2a50', glow: 'rgba(216,180,254,.17)', desk: 'rgba(78,32,91,.4)', felt: 'linear-gradient(145deg, #6a347d, #3f1c4c 68%)' },
};

export const createAppTheme = (name = 'teal') => {
  const colors = THEME_COLORS[name] || THEME_COLORS.teal;
  return createTheme({
  palette: {
    mode: 'dark',
    primary: { main: colors.primary, contrastText: SHARED_DESIGN.accentText },
    secondary: { main: colors.secondary, contrastText: '#241300' },
    success: { main: SHARED_DESIGN.success },
    error: { main: SHARED_DESIGN.danger },
    background: { default: colors.bg, paper: colors.surface },
    text: { primary: SHARED_DESIGN.text, secondary: SHARED_DESIGN.muted },
    divider: SHARED_DESIGN.border,
  },
  typography: {
    fontFamily: "'Noto Sans Myanmar', 'Noto Sans JP', 'Segoe UI', Roboto, sans-serif",
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.6 },
    button: { fontWeight: 700, lineHeight: 1.5 },
    h5: { fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.45 },
    h6: { fontWeight: 750, lineHeight: 1.45 },
  },
  shape: { borderRadius: SHARED_DESIGN.radius },
  components: {
    MuiModal: {
      defaultProps: { manager: accessibleModalManager, disableRestoreFocus: true },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 999, textTransform: 'none', minHeight: 40, whiteSpace: 'nowrap' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${SHARED_DESIGN.border}`,
          backgroundImage: `linear-gradient(145deg, ${colors.raised}, ${colors.surface})`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: `1px solid ${SHARED_DESIGN.border}`,
          backgroundImage: `linear-gradient(145deg, ${colors.raised}, ${colors.surface})`,
          boxShadow: '0 24px 70px rgba(0,0,0,.55)',
        },
      },
    },
    MuiTextField: {
      defaultProps: { color: 'primary' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { overflow: 'visible' },
        input: { lineHeight: 1.55 },
        notchedOutline: { borderColor: SHARED_DESIGN.border, overflow: 'visible' },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { color: SHARED_DESIGN.muted, lineHeight: 1.5, overflow: 'visible' } },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { lineHeight: 1.55, overflowWrap: 'anywhere' } },
    },
    MuiMenu: {
      styleOverrides: { paper: { border: `1px solid ${SHARED_DESIGN.border}` } },
    },
  },
  });
};

export default createAppTheme();
